import React, { createContext, useContext, useState } from "react";
import { authenticateApp } from "./auth-context";

// ---------------- Types ---------------- //

interface PanchkarmaDetails {
  date: string;
  roomType: string;
  hasCompanion: boolean;
  companionName: string;
  companionAge: string;
  companionGender: string;
  needCab: boolean;
  pickupAddress: string;
  pickupLandmark: string;
  pickupContactNumber: string;
  clinicName: string;
  clinicId: string;
  clinicAddress: string;
}

interface CardDetails {
  name: string;
  cardnumber: string;
  expiryDate: string;
  cvv: string;
  shouldSaveCard: boolean;
  nicknameOption: string;
  nickNametext: string;
}

interface IPanchkarmaContext {
  savePanchkarmaDetails: PanchkarmaDetails;
  setSavePanchkarmaDetails: React.Dispatch<
    React.SetStateAction<PanchkarmaDetails>
  >;
  cardDetails: CardDetails;
  setCardDetails: React.Dispatch<React.SetStateAction<CardDetails>>;
}

// ---------------- Context Setup ---------------- //

const PanchkarmaContext = createContext<IPanchkarmaContext | undefined>(
  undefined
);

// ---------------- Custom Provider Hook ---------------- //

const usePanchkarmaProvider = () => {
  const { clinicUserId } = authenticateApp(); // in case you need it in future

  const [savePanchkarmaDetails, setSavePanchkarmaDetails] =
    useState<PanchkarmaDetails>({
      clinicName: "",
      clinicAddress: "",
      clinicId: "",
      date: "",
      roomType: "",
      hasCompanion: false,
      companionName: "",
      companionAge: "",
      companionGender: "",
      needCab: true,
      pickupAddress: "",
      pickupLandmark: "",
      pickupContactNumber: "",
    });

  const [cardDetails, setCardDetails] = useState<CardDetails>({
    name: "",
    cardnumber: "",
    expiryDate: "",
    cvv: "",
    shouldSaveCard: true,
    nicknameOption: "",
    nickNametext: "",
  });
  return {
    savePanchkarmaDetails,
    setSavePanchkarmaDetails,
    cardDetails,
    setCardDetails,
  };
};

// ---------------- Provider Component ---------------- //

interface Props {
  children: React.ReactNode;
}

export const PanchkarmaProvider: React.FC<Props> = ({ children }) => {
  const contextValue = usePanchkarmaProvider();

  return (
    <PanchkarmaContext.Provider value={contextValue}>
      {children}
    </PanchkarmaContext.Provider>
  );
};

// ---------------- Custom Hook for Consumer ---------------- //

export const usePanchkarma = (): IPanchkarmaContext => {
  const context = useContext(PanchkarmaContext);
  if (!context) {
    throw new Error("usePanchkarma must be used within a PanchkarmaProvider");
  }
  return context;
};
